import {
	type ReactNode,
	useState,
	useRef,
	useEffect,
	useCallback
} from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import {
	Plus,
	Search,
	MoreVertical,
	Pencil,
	CheckSquare,
	FolderInput,
	ChevronUp,
	ChevronDown,
	ChevronLeft,
	ChevronRight,
	Trash2
} from "lucide-react";

export interface ContentTableItem {
	id: number | string;
	name: string;
	createdBy?: string;
	lastUpdated?: string;
	isActive: boolean;
	order?: number;
	data?: Record<string, unknown>;
}

export interface ContentTableColumn {
	key: string;
	label: string;
	render: (item: ContentTableItem) => ReactNode;
	width?: string;
}

export interface ContentTableProps {
	columns?: ContentTableColumn[];
	items: ContentTableItem[];
	onRowClick?: (item: ContentTableItem) => void;
	onMakeLive?: (item: ContentTableItem) => void;
	onMakeDraft?: (item: ContentTableItem) => void;
	onDelete?: (item: ContentTableItem) => void;
	onMoveUp?: (item: ContentTableItem) => void;
	onMoveDown?: (item: ContentTableItem) => void;
	createPath?: string;
	createLabel?: string;
	searchPlaceholder?: string;
	showOrder?: boolean;
	showCreatedBy?: boolean;
	showLastUpdated?: boolean;
	showStatus?: boolean;
	filterSlot?: React.ReactNode;
	nameColumnLabel?: string;
	renderName?: (item: ContentTableItem) => React.ReactNode;
	/** When provided, search is controlled externally (server-side). Client-side filtering is skipped. */
	searchValue?: string;
	/** Called when the search input changes. Use with searchValue for server-side search. */
	onSearchChange?: (value: string) => void;
	/** When provided, controls whether delete is shown per row. Defaults to always showing if onDelete is set. */
	canDelete?: (item: ContentTableItem) => boolean;
	/** Show skeleton loading rows instead of "No content found" */
	isLoading?: boolean;
}

interface RowMenuProps {
	item: ContentTableItem;
	isFirstLive: boolean;
	isLastLive: boolean;
	onRowClick?: (item: ContentTableItem) => void;
	onMakeLive?: (item: ContentTableItem) => void;
	onMakeDraft?: (item: ContentTableItem) => void;
	onDelete?: (item: ContentTableItem) => void;
	canDelete?: (item: ContentTableItem) => boolean;
	onMoveUp?: (item: ContentTableItem) => void;
	onMoveDown?: (item: ContentTableItem) => void;
}

const RowMenu = ({
	item,
	isFirstLive,
	isLastLive,
	onRowClick,
	onMakeLive,
	onMakeDraft,
	onDelete,
	canDelete,
	onMoveUp,
	onMoveDown
}: RowMenuProps) => {
	const [isOpen, setIsOpen] = useState(false);
	const buttonRef = useRef<HTMLButtonElement>(null);
	const menuRef = useRef<HTMLDivElement>(null);
	const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });

	const updatePosition = useCallback(() => {
		if (!buttonRef.current) return;
		const rect = buttonRef.current.getBoundingClientRect();
		setMenuPos({
			top: rect.bottom + 4,
			left: rect.right - 176
		});
	}, []);

	useEffect(() => {
		const handleOutsideClick = (event: globalThis.MouseEvent) => {
			if (
				menuRef.current &&
				!menuRef.current.contains(event.target as Node) &&
				buttonRef.current &&
				!buttonRef.current.contains(event.target as Node)
			) {
				setIsOpen(false);
			}
		};

		if (isOpen) {
			document.addEventListener("mousedown", handleOutsideClick);
			window.addEventListener("scroll", () => setIsOpen(false), true);
		}

		return () => {
			document.removeEventListener("mousedown", handleOutsideClick);
			window.removeEventListener("scroll", () => setIsOpen(false), true);
		};
	}, [isOpen]);

	// Focus the first menu item when the menu opens
	useEffect(() => {
		if (isOpen && menuRef.current) {
			const firstItem =
				menuRef.current.querySelector<HTMLButtonElement>('[role="menuitem"]');
			if (firstItem) {
				firstItem.focus();
			}
		}
	}, [isOpen]);

	const handleMenuKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
		const menu = menuRef.current;
		if (!menu) return;

		const items = Array.from(
			menu.querySelectorAll<HTMLButtonElement>('[role="menuitem"]')
		);
		const currentIndex = items.indexOf(
			document.activeElement as HTMLButtonElement
		);

		switch (e.key) {
			case "Escape":
				e.preventDefault();
				e.stopPropagation();
				setIsOpen(false);
				buttonRef.current?.focus();
				break;
			case "ArrowDown":
				e.preventDefault();
				if (currentIndex < items.length - 1) {
					items[currentIndex + 1].focus();
				} else {
					items[0].focus();
				}
				break;
			case "ArrowUp":
				e.preventDefault();
				if (currentIndex > 0) {
					items[currentIndex - 1].focus();
				} else {
					items[items.length - 1].focus();
				}
				break;
			case "Tab":
				setIsOpen(false);
				break;
		}
	};

	const handleMenuToggle = (e: React.MouseEvent) => {
		e.stopPropagation();
		updatePosition();
		setIsOpen((prev) => !prev);
	};

	const handleAction = (
		e: React.MouseEvent | React.KeyboardEvent,
		action: () => void
	) => {
		e.stopPropagation();
		setIsOpen(false);
		buttonRef.current?.focus();
		action();
	};

	return (
		<div className="relative">
			<button
				ref={buttonRef}
				onClick={handleMenuToggle}
				aria-label={`Actions for ${item.name}`}
				aria-haspopup="menu"
				aria-expanded={isOpen}
				className="p-2 rounded-lg hover:bg-muted focus:bg-muted flex items-center justify-center"
			>
				<MoreVertical size={16} />
			</button>

			{isOpen &&
				createPortal(
					<div
						ref={menuRef}
						aria-label="Row actions"
						role="menu"
						onKeyDown={handleMenuKeyDown}
						className="fixed w-44 bg-white border border-border rounded-md shadow-lg z-50 py-1"
						style={{ top: menuPos.top, left: menuPos.left }}
					>
						{onRowClick && (
							<button
								role="menuitem"
								tabIndex={-1}
								onClick={(e) => handleAction(e, () => onRowClick(item))}
								className="flex items-center w-full px-4 py-2 text-sm hover:bg-muted text-left"
							>
								<Pencil size={16} />
								<span className="ml-3">Edit</span>
							</button>
						)}

						{!item.isActive && onMakeLive && (
							<button
								role="menuitem"
								tabIndex={-1}
								onClick={(e) => handleAction(e, () => onMakeLive(item))}
								className="flex items-center w-full px-4 py-2 text-sm hover:bg-muted text-left"
							>
								<CheckSquare size={16} />
								<span className="ml-3">Make Live</span>
							</button>
						)}

						{item.isActive && onMakeDraft && (
							<button
								role="menuitem"
								tabIndex={-1}
								onClick={(e) => handleAction(e, () => onMakeDraft(item))}
								className="flex items-center w-full px-4 py-2 text-sm hover:bg-muted text-left"
							>
								<FolderInput size={16} />
								<span className="ml-3">Make Draft</span>
							</button>
						)}

						{item.isActive && !isFirstLive && onMoveUp && (
							<button
								role="menuitem"
								tabIndex={-1}
								onClick={(e) => handleAction(e, () => onMoveUp(item))}
								className="flex items-center w-full px-4 py-2 text-sm hover:bg-muted text-left"
							>
								<ChevronUp size={16} />
								<span className="ml-3">Move Up</span>
							</button>
						)}

						{item.isActive && !isLastLive && onMoveDown && (
							<button
								role="menuitem"
								tabIndex={-1}
								onClick={(e) => handleAction(e, () => onMoveDown(item))}
								className="flex items-center w-full px-4 py-2 text-sm hover:bg-muted text-left"
							>
								<ChevronDown size={16} />
								<span className="ml-3">Move Down</span>
							</button>
						)}

						{onDelete && (!canDelete || canDelete(item)) && (
							<button
								role="menuitem"
								tabIndex={-1}
								onClick={(e) => handleAction(e, () => onDelete(item))}
								className="flex items-center w-full px-4 py-2 text-sm hover:bg-muted text-left text-destructive"
							>
								<Trash2 size={16} />
								<span className="ml-3">Delete</span>
							</button>
						)}
					</div>,
					document.body
				)}
		</div>
	);
};

const ContentTable = ({
	columns,
	items,
	onRowClick,
	onMakeLive,
	onMakeDraft,
	onDelete,
	canDelete,
	onMoveUp,
	onMoveDown,
	createPath,
	createLabel = "Create",
	searchPlaceholder = "Search...",
	showOrder = true,
	showCreatedBy = true,
	showLastUpdated = true,
	showStatus = true,
	filterSlot,
	nameColumnLabel = "Content name",
	renderName,
	searchValue,
	onSearchChange,
	isLoading = false
}: ContentTableProps) => {
	const columnCount =
		1 +
		(columns
			? columns.length
			: (showCreatedBy ? 1 : 0) +
				(showLastUpdated ? 1 : 0) +
				(showOrder ? 1 : 0)) +
		(showStatus ? 1 : 0) +
		1;

	const [internalSearch, setInternalSearch] = useState("");
	const isControlledSearch = searchValue !== undefined;
	const hasControlledHandler = typeof onSearchChange === "function";
	const search = isControlledSearch ? searchValue : internalSearch;
	const handleSearchChange =
		isControlledSearch && hasControlledHandler
			? onSearchChange
			: isControlledSearch
				? // eslint-disable-next-line @typescript-eslint/no-empty-function
					() => {}
				: setInternalSearch;

	useEffect(() => {
		if (
			process.env.NODE_ENV !== "production" &&
			isControlledSearch &&
			!hasControlledHandler
		) {
			console.error(
				"ContentTable: `searchValue` was provided without `onSearchChange`. Controlled search inputs require an `onSearchChange` handler."
			);
		}
	}, [isControlledSearch, hasControlledHandler]);
	const [page, setPage] = useState(1);
	const pageSize = 20;

	// When custom columns are provided, the caller controls sort order entirely
	const sorted = columns
		? items
		: (() => {
				const live = items
					.filter((item) => item.isActive)
					.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
				const draft = items
					.filter((item) => !item.isActive)
					.sort((a, b) => a.name.localeCompare(b.name));
				return [...live, ...draft];
			})();

	// Skip client-side filtering when search is controlled externally (server-side)
	const filtered =
		!isControlledSearch && search.trim()
			? sorted.filter((item) =>
					item.name.toLowerCase().includes(search.toLowerCase())
				)
			: sorted;

	const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
	const safeCurrentPage = Math.min(page, totalPages);
	const paged = filtered.slice(
		(safeCurrentPage - 1) * pageSize,
		safeCurrentPage * pageSize
	);

	const liveItemIds = new Set(
		items.filter((item) => item.isActive).map((item) => item.id)
	);

	const pagedLiveItems = paged.filter((item) => liveItemIds.has(item.id));

	// Reset to page 1 when search or items change
	useEffect(() => {
		setPage(1);
	}, [search, items.length]);

	const formatDate = (date?: string) => {
		if (!date) return "—";
		try {
			return new Date(date).toLocaleDateString("en-GB", {
				day: "numeric",
				month: "short",
				year: "numeric"
			});
		} catch {
			return date;
		}
	};

	return (
		<div>
			<div className="flex items-center justify-between mb-6">
				<div
					className={`relative w-full${createPath ? " max-w-sm" : " max-w-full md:max-w-sm"}`}
				>
					<Search
						size={16}
						className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
						aria-hidden="true"
					/>
					<input
						type="text"
						aria-label={searchPlaceholder}
						placeholder={searchPlaceholder}
						value={search}
						onChange={(e) => handleSearchChange(e.target.value)}
						className="rounded-lg border border-border bg-white pl-9 pr-4 py-2 text-base w-full shadow-xs focus:outline-none focus:ring-2 focus:ring-ring"
					/>
				</div>

				{createPath && (
					<Link
						to={createPath}
						className="ml-4 inline-flex items-center bg-primary text-white rounded-lg px-4 py-2.5 text-sm font-medium hover:bg-primary-dark transition-colors whitespace-nowrap"
					>
						<Plus className="h-4 w-4 mr-2" aria-hidden="true" />
						{createLabel}
					</Link>
				)}
			</div>

			{filterSlot && <div className="mb-4">{filterSlot}</div>}

			<div className="overflow-x-auto rounded-lg border border-border shadow-sm">
				<table
					className="w-full text-sm table-fixed"
					aria-label={`${nameColumnLabel} table`}
				>
					<thead>
						<tr className="bg-white text-secondary-foreground text-sm font-medium border-b border-border">
							<th className="text-left px-4 py-3">{nameColumnLabel}</th>
							{columns ? (
								columns.map((col) => (
									<th
										key={col.key}
										className="text-left px-4 py-3"
										style={col.width ? { width: col.width } : undefined}
									>
										{col.label}
									</th>
								))
							) : (
								<>
									{showCreatedBy && (
										<th className="text-left px-4 py-3">Created by</th>
									)}
									{showLastUpdated && (
										<th className="text-left px-4 py-3">Last updated</th>
									)}
									{showOrder && (
										<th className="text-left px-4 py-3 w-16">Order</th>
									)}
								</>
							)}
							{showStatus && <th className="text-left px-4 py-3">Status</th>}
							<th className="px-4 py-3 w-12">
								<span className="sr-only">Actions</span>
							</th>
						</tr>
					</thead>

					<tbody>
						{paged.length === 0 && isLoading && (
							<>
								{Array.from({ length: 8 }).map((_, i) => (
									<tr key={`skeleton-${i}`}>
										{Array.from({
											length: columnCount
										}).map((__, j) => (
											<td key={j} className="px-4 py-3">
												<div className="h-4 bg-zinc-100 rounded animate-pulse" />
											</td>
										))}
									</tr>
								))}
							</>
						)}

						{paged.length === 0 && !isLoading && (
							<tr>
								<td
									colSpan={columnCount}
									className="text-center py-12 text-muted-foreground"
								>
									No content found
								</td>
							</tr>
						)}

						{paged.map((item) => {
							const liveIndex = pagedLiveItems.findIndex(
								(li) => li.id === item.id
							);
							const isFirstLive = liveIndex === 0;
							const isLastLive = liveIndex === pagedLiveItems.length - 1;

							return (
								<tr
									key={item.id}
									onClick={onRowClick ? () => onRowClick(item) : undefined}
									onKeyDown={
										onRowClick
											? (e) => {
													if (e.key === "Enter") onRowClick(item);
												}
											: undefined
									}
									tabIndex={onRowClick ? 0 : undefined}
									role={onRowClick ? "link" : undefined}
									className={`even:bg-zebra hover:bg-primary-lighter transition-colors border-b border-border last:border-b-0${onRowClick ? " cursor-pointer" : ""}`}
								>
									<td className="px-4 py-3 font-medium">
										{renderName ? renderName(item) : item.name}
									</td>
									{columns ? (
										columns.map((col) => (
											<td
												key={col.key}
												className="px-4 py-3 text-muted-foreground truncate"
											>
												{col.render(item)}
											</td>
										))
									) : (
										<>
											{showCreatedBy && (
												<td className="px-4 py-3 text-muted-foreground">
													{item.createdBy ?? "—"}
												</td>
											)}
											{showLastUpdated && (
												<td className="px-4 py-3 text-muted-foreground">
													{formatDate(item.lastUpdated)}
												</td>
											)}
											{showOrder && (
												<td className="px-4 py-3 text-muted-foreground">
													{item.isActive &&
													item.order !== null &&
													item.order !== undefined
														? item.order
														: "—"}
												</td>
											)}
										</>
									)}
									{showStatus && (
										<td className="px-4 py-3">
											{item.isActive ? (
												<span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium bg-emerald-50 text-emerald-700">
													Live
												</span>
											) : (
												<span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium bg-muted text-muted-foreground">
													Draft
												</span>
											)}
										</td>
									)}
									<td className="px-4 py-3">
										<RowMenu
											item={item}
											isFirstLive={isFirstLive}
											isLastLive={isLastLive}
											onRowClick={onRowClick}
											onMakeLive={onMakeLive}
											onMakeDraft={onMakeDraft}
											onDelete={onDelete}
											canDelete={canDelete}
											onMoveUp={onMoveUp}
											onMoveDown={onMoveDown}
										/>
									</td>
								</tr>
							);
						})}
					</tbody>
				</table>
			</div>

			{totalPages > 1 && (
				<div className="flex items-center justify-between mt-4 text-sm">
					<p className="text-muted-foreground">
						Showing {(safeCurrentPage - 1) * pageSize + 1} to{" "}
						{Math.min(safeCurrentPage * pageSize, filtered.length)} of{" "}
						{filtered.length} items
					</p>
					<div className="flex items-center gap-2">
						<button
							onClick={() => setPage((p) => Math.max(1, p - 1))}
							disabled={safeCurrentPage <= 1}
							className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-border text-sm font-medium hover:bg-muted disabled:opacity-40 disabled:pointer-events-none transition-colors"
						>
							<ChevronLeft className="h-4 w-4" />
							Previous
						</button>
						<span className="text-muted-foreground px-2">
							{safeCurrentPage} of {totalPages}
						</span>
						<button
							onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
							disabled={safeCurrentPage >= totalPages}
							className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-border text-sm font-medium hover:bg-muted disabled:opacity-40 disabled:pointer-events-none transition-colors"
						>
							Next
							<ChevronRight className="h-4 w-4" />
						</button>
					</div>
				</div>
			)}
		</div>
	);
};

export default ContentTable;
